<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import PdsButton from '@/components/pds/PdsButton.vue';
import BoardBar from '../components/BoardBar.vue';
import BoardColumn from '../components/BoardColumn.vue';
import EmptyState from '../components/EmptyState.vue';
import MenuPopover from '../components/MenuPopover.vue';
import OrderTicket from '../components/OrderTicket.vue';
import { usePdsOrdersStore } from '../stores/orders.store';
import type { PdsOrder, PdsStageFilter } from '../types';

const store = usePdsOrdersStore();
const router = useRouter();
const menuOpen = ref(false);

const shows = (stage: Exclude<PdsStageFilter, 'all'>) =>
  store.stageFilter === 'all' || store.stageFilter === stage;

const alone = computed(() => store.stageFilter !== 'all');

// Dans chaque colonne, une seule carte est dépliée : en préparation la première tant qu'on n'a rien touché,
// ailleurs aucune.
const useAccordion = (list: () => PdsOrder[], firstOpen: boolean) => {
  // null = l'opérateur a tout replié ; undefined = rien choisi, la première carte s'ouvre.
  const chosenId = ref<string | null | undefined>(undefined);

  // La carte ouverte par défaut est retenue dès qu'elle existe : sans cela, une commande plus urgente qui arrive
  // devient « la première » et la carte ouverte change toute seule.
  watch(
    list,
    (orders) => {
      if (chosenId.value === null) return;
      if (orders.some((o) => o.id === chosenId.value)) return;
      chosenId.value = firstOpen ? orders[0]?.id : undefined;
    },
    { immediate: true },
  );

  const unfoldedId = computed(() => chosenId.value ?? null);
  const fold = (orderId: string) => {
    chosenId.value = unfoldedId.value === orderId ? null : orderId;
  };
  return { unfoldedId, fold };
};

const newAccordion = useAccordion(() => store.newOrders, false);
const preparingAccordion = useAccordion(() => store.preparingOrders, true);
const readyAccordion = useAccordion(() => store.readyOrders, false);

const open = (orderId: string) =>
  router.push({ name: 'pds-order', params: { id: orderId } });
</script>

<template>
  <BoardBar @menu="menuOpen = true" />

  <main
    class="no-scrollbar flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pt-2 pb-6 sm:px-5 lg:flex-row lg:overflow-hidden lg:pb-0"
  >
    <div
      v-if="store.loadStatus !== 'ready'"
      class="m-auto flex max-w-[420px] flex-col items-center gap-4 text-center"
      :role="store.loadStatus === 'error' ? 'alert' : 'status'"
    >
      <template v-if="store.loadStatus === 'error'">
        <p class="text-[14px] leading-[1.4]">
          Les commandes n’ont pas pu être chargées. Vérifiez le réseau, puis
          réessayez.
        </p>
        <PdsButton fit @click="store.retryLoad()">Réessayer</PdsButton>
      </template>
      <p v-else class="text-encre-douce text-[14px] leading-[1.4]">
        Chargement des commandes…
      </p>
    </div>

    <EmptyState
      v-else-if="!store.activeCount && store.stageFilter === 'all'"
      class="m-auto"
      icon="inbox"
      title="Aucune commande pour le moment"
      text="Le tableau se remplit tout seul dès qu’une commande arrive."
    />

    <template v-else>
      <BoardColumn
        v-if="shows('new')"
        title="Nouvelles"
        :count="store.newOrders.length"
        :spread="alone"
      >
        <EmptyState
          v-if="!store.newOrders.length"
          class="col-span-full"
          icon="inbox"
          title="Aucune nouvelle commande"
          text="Elles arrivent ici dès qu’un client commande."
        />
        <OrderTicket
          v-for="order in store.newOrders"
          :key="order.id"
          :order="order"
          :late="store.isLate(order)"
          :unfolded="newAccordion.unfoldedId.value === order.id"
          @fold="newAccordion.fold(order.id)"
          @open="open(order.id)"
        />
      </BoardColumn>

      <BoardColumn
        v-if="shows('preparing')"
        title="En préparation"
        :count="store.preparingOrders.length"
        :spread="alone"
      >
        <EmptyState
          v-if="!store.preparingOrders.length"
          class="col-span-full"
          icon="clock"
          title="Rien en préparation"
          text="Acceptez une nouvelle commande pour la préparer."
        />
        <OrderTicket
          v-for="order in store.preparingOrders"
          :key="order.id"
          :order="order"
          :late="store.isLate(order)"
          :unfolded="preparingAccordion.unfoldedId.value === order.id"
          @fold="preparingAccordion.fold(order.id)"
          @open="open(order.id)"
          @toggle-item="store.toggleItem(order.id, $event)"
          @mark-ready="store.requestReady(order.id)"
        />
      </BoardColumn>

      <BoardColumn
        v-if="shows('ready')"
        title="Prêtes"
        :count="store.readyOrders.length"
        :spread="alone"
      >
        <EmptyState
          v-if="!store.readyOrders.length"
          class="col-span-full"
          icon="bag"
          title="Aucune commande prête"
          text="Une commande marquée prête attend ici son client."
        />
        <OrderTicket
          v-for="order in store.readyOrders"
          :key="order.id"
          :order="order"
          :late="false"
          :unfolded="readyAccordion.unfoldedId.value === order.id"
          @fold="readyAccordion.fold(order.id)"
          @hand-over="store.handOver(order.id)"
        />
      </BoardColumn>
    </template>
  </main>

  <MenuPopover v-if="menuOpen" @close="menuOpen = false" />
</template>
